import { useState } from 'react';
import { X, CheckCircle2, Loader2 } from 'lucide-react';

export const ContactDialog = ({ open, onOpenChange }) => {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const formData = new FormData(e.target);

    try {
      const response = await fetch('https://formspree.io/f/xreywwda', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        setSubmitted(true);
        e.target.reset();
      } else {
        const data = await response.json();
        setError(data.error || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
      }
    } catch (err) {
      setError('Verbindungsfehler. Bitte prüfen Sie Ihre Internetverbindung.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSubmitted(false);
    setError(null);
    onOpenChange(false);
  };

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button
          type="button"
          onClick={handleClose}
          className="modal-close-btn"
          aria-label="Schließen"
        >
          <X size={20} />
        </button>

        {submitted ? (
          /* Success State */
          <div className="modal-success">
            <div className="modal-success-icon">
              <CheckCircle2 size={40} color="#10B981" />
            </div>
            <h2>Vielen Dank für Ihre Anfrage</h2>
            <p>Wir haben Ihre Nachricht erhalten und werden uns innerhalb von 24 Stunden bei Ihnen melden.</p>
            <button type="button" onClick={handleClose} className="btn-submit">
              Schließen
            </button>
          </div>
        ) : (
          <>
            <div className="modal-header">
              <h2>Beratungsgespräch anfragen</h2>
              <p>Füllen Sie das Formular aus und wir erstellen eine individuelle Potenzialanalyse für Ihr Krankenhaus.</p>
            </div>

            {error && (
              <div className="modal-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="modal-form">
              {/* Krankenhaus-Informationen */}
              <div className="form-section">
                <h3 className="form-section-title">Krankenhaus-Informationen</h3>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="krankenhaus">
                      Name des Krankenhauses <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="krankenhaus"
                      name="Krankenhaus"
                      placeholder="z.B. Städtisches Klinikum"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="betten">Anzahl der Betten</label>
                    <select id="betten" name="Anzahl_Betten" defaultValue="">
                      <option value="" disabled>Bitte auswählen</option>
                      <option value="Unter 100 Betten">Unter 100 Betten</option>
                      <option value="100 - 300 Betten">100 - 300 Betten</option>
                      <option value="300 - 500 Betten">300 - 500 Betten</option>
                      <option value="500 - 1000 Betten">500 - 1.000 Betten</option>
                      <option value="Über 1000 Betten">Über 1.000 Betten</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="stromverbrauch">Jährlicher Stromverbrauch</label>
                    <select id="stromverbrauch" name="Stromverbrauch" defaultValue="">
                      <option value="" disabled>Bitte auswählen</option>
                      <option value="Unter 1 Mio kWh">Unter 1 Mio. kWh</option>
                      <option value="1 - 3 Mio kWh">1 - 3 Mio. kWh</option>
                      <option value="3 - 5 Mio kWh">3 - 5 Mio. kWh</option>
                      <option value="5 - 10 Mio kWh">5 - 10 Mio. kWh</option>
                      <option value="Über 10 Mio kWh">Über 10 Mio. kWh</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="pv">Bestehende PV-Anlage</label>
                    <select id="pv" name="PV_Anlage" defaultValue="">
                      <option value="" disabled>Bitte auswählen</option>
                      <option value="Ja vorhanden">Ja, vorhanden</option>
                      <option value="In Planung">In Planung</option>
                      <option value="Nein">Nein</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Kontaktperson */}
              <div className="form-section">
                <h3 className="form-section-title">Kontaktperson</h3>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">
                      Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="Name"
                      placeholder="Vor- und Nachname"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">
                      E-Mail <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="ihre.email@krankenhaus.de"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="telefon">Telefon</label>
                  <input
                    type="tel"
                    id="telefon"
                    name="Telefon"
                    placeholder="+49 ..."
                  />
                </div>
              </div>

              {/* Nachricht */}
              <div className="form-group">
                <label htmlFor="nachricht">Ihre Nachricht / Spezifische Anforderungen</label>
                <textarea
                  id="nachricht"
                  name="Nachricht"
                  placeholder="Optional: Beschreiben Sie Ihre spezifischen Anforderungen oder Fragen..."
                  rows={4}
                />
              </div>

              {/* Datenschutz Checkbox */}
              <div className="form-checkbox">
                <input
                  type="checkbox"
                  id="datenschutz"
                  name="Datenschutz_Zustimmung"
                  value="Ja"
                  required
                />
                <label htmlFor="datenschutz">
                  Ich stimme der Verarbeitung meiner Daten zum Zweck der Kontaktaufnahme zu. 
                  Weitere Informationen finden Sie in unserer Datenschutzerklärung. <span className="required">*</span>
                </label>
              </div>

              {/* Buttons */}
              <div className="form-buttons">
                <button
                  type="button"
                  onClick={handleClose}
                  className="btn-cancel"
                  disabled={isSubmitting}
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={16} className="spinner" />
                      Wird gesendet...
                    </>
                  ) : (
                    'Anfrage senden'
                  )}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
